/**
 * UX-PLAN Pillar F — per-session queue mode resolver.
 *
 * The composer had a single global queue mode (`fleetDefaultQueueMode`)
 * that leaked across sessions. Switching sessions should not change
 * whether the next message is queued, steered, or interrupts a run —
 * so the mode now has a per-session mirror (`fleetDefaultQueueModeBySession`)
 * that wins when present.
 *
 * Pure function. Reads nothing; takes the settings snapshot and the
 * active session key, returns the resolved mode.
 */

export type FleetQueueMode = "steer" | "collect" | "followup" | "interrupt" | "steer-backlog";

export const FLEET_QUEUE_MODES: readonly FleetQueueMode[] = [
  "steer",
  "collect",
  "followup",
  "interrupt",
  "steer-backlog",
];

export function resolveFleetQueueMode(
  bySession: Record<string, FleetQueueMode> | undefined | null,
  globalMode: FleetQueueMode,
  sessionKey: string | null | undefined,
): FleetQueueMode {
  if (sessionKey) {
    const key = sessionKey.trim();
    if (key) {
      const override = bySession?.[key];
      if (override && (FLEET_QUEUE_MODES as readonly string[]).includes(override)) {
        return override;
      }
    }
  }
  return globalMode;
}

/**
 * Returns a new map with the per-session override set; preserves
 * type-safety by validating against {@link FLEET_QUEUE_MODES}.
 */
export function setFleetQueueModeForSession(
  bySession: Record<string, FleetQueueMode> | undefined | null,
  sessionKey: string,
  mode: FleetQueueMode,
): Record<string, FleetQueueMode> {
  const next: Record<string, FleetQueueMode> = { ...(bySession ?? {}) };
  const key = sessionKey.trim();
  if (!key) {
    return next;
  }
  if ((FLEET_QUEUE_MODES as readonly string[]).includes(mode)) {
    next[key] = mode;
  }
  return next;
}
